import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { StarRating } from "@/components/StarRating";
import { ShieldCheck, MapPin, Sparkles, Users, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import QuickBook from "@/components/QuickBook";

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      setVehicles(r.data);
      const byId = Object.fromEntries(r.data.map((v) => [v.vehicle_id, v.name]));
      api.get("/reviews").then((rr) => {
        setReviews(rr.data.map((rev) => ({ ...rev, vehicle_name: byId[rev.vehicle_id] || "" })));
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      <Seo
        title="B&L Rentals"
        description="Rent a Ford Transit passenger van or Transit Connect in Oxnard, CA. 5-star Turo host with 200+ trips serving all of Ventura County. Unlimited miles, auto-applied multi-day discounts."
        canonical={typeof window !== "undefined" ? window.location.origin + "/" : undefined}
        image={typeof window !== "undefined" ? window.location.origin + "/hero-van.jpg" : undefined}
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "B&L Rentals",
          "image": "https://images.pexels.com/photos/28561862/pexels-photo-28561862.jpeg",
          "priceRange": "$$",
          "telephone": "+1-805-290-8431",
          "email": "hello@blrental.com",
          "address": {"@type": "PostalAddress", "streetAddress": "Oxnard", "addressLocality": "Oxnard", "addressRegion": "CA", "postalCode": "93030", "addressCountry": "US"},
          "areaServed": ["Oxnard", "Ventura", "Camarillo", "Port Hueneme", "Ventura County"],
          "aggregateRating": {"@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "212"}
        }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="/hero-van.jpg"
            alt="Ford Transit passenger van at a California beach with a family loading luggage"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#010F27] via-[#04162F]/90 to-[#04162F]/40" />
        </div>
        <div className="container-bl pt-8 pb-24 md:pt-14 md:pb-36">
          {/* Credibility bar */}
          <div className="inline-flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="hero-credibility">
            <div className="flex items-center gap-1 text-sm font-semibold text-white">
              <span className="text-[#E2AB20]">★★★★★</span>
              <span className="ml-1">5-Star Turo Host</span>
            </div>
            <span className="text-white/50">·</span>
            <span className="text-sm font-semibold text-white">200+ Trips</span>
            <span className="text-white/50">·</span>
            <span className="text-sm font-semibold text-[#E2AB20]">Ventura County's Most Dependable Rental Host</span>
          </div>

          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold font-heading tracking-tight leading-[1.15] text-white max-w-3xl" data-testid="hero-headline">
            Ready to hit the road?
          </h1>
          <p className="mt-3 text-lg md:text-xl text-[#AEB9C9] max-w-2xl" data-testid="hero-subtitle">
            Choose your vehicle, select your dates, and start your journey.
          </p>

          <div className="mt-5 md:mt-6">
            <QuickBook />
          </div>
        </div>
      </section>

      {/* FEATURED VEHICLES */}
      <section className="container-bl py-16 md:py-24">
        {/* Multi-day deals strip */}
        <div className="mb-12 rounded-2xl bg-gradient-to-r from-[#E2AB20] to-[#E2AB20] text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" data-testid="home-deals-strip">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-white/80">Auto-applied deals</div>
            <h3 className="mt-1 font-heading text-xl md:text-2xl font-bold">Rent longer, save more — automatically.</h3>
            <p className="mt-1 text-sm text-white/85">No promo code needed. Discounts apply the moment your date range hits the threshold. Each vehicle shows its own tiered savings.</p>
          </div>
          <div className="grid grid-cols-3 gap-3 w-full md:w-auto">
            <div className="rounded-lg bg-white/10 backdrop-blur px-4 py-3 text-center">
              <div className="text-[11px] uppercase tracking-widest text-white/80">3+ days</div>
              <div className="mt-0.5 font-heading font-bold">Save</div>
            </div>
            <div className="rounded-lg bg-white/10 backdrop-blur px-4 py-3 text-center">
              <div className="text-[11px] uppercase tracking-widest text-white/80">7+ days</div>
              <div className="mt-0.5 font-heading font-bold">Save more</div>
            </div>
            <div className="rounded-lg bg-white/10 backdrop-blur px-4 py-3 text-center">
              <div className="text-[11px] uppercase tracking-widest text-white/80">14+ days</div>
              <div className="mt-0.5 font-heading font-bold">Save most</div>
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-sm font-semibold uppercase tracking-widest text-[#E2AB20]">Our Fleet</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold font-heading text-white">Vehicles built for Ventura County trips</h2>
          </div>
          <Link to="/vehicles" className="link-blue text-sm font-semibold" data-testid="home-see-all-vehicles">See all vehicles →</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {vehicles.map((v) => (
            <Link
              key={v.vehicle_id}
              to={`/vehicles/${v.slug}`}
              data-testid={`vehicle-card-${v.slug}`}
              className="card-bl group transition-transform hover:-translate-y-1 hover:shadow-md"
            >
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                  src={v.hero_image}
                  alt={`Rent a ${v.name} in Oxnard`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">{v.type}</div>
                  <StarRating value={v.rating || 5} size={12} />
                </div>
                <h3 className="mt-2 font-heading font-semibold text-lg text-white">{v.name}</h3>
                <p className="mt-1 text-sm text-[#AEB9C9] line-clamp-2">{v.short_desc}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-white"><span className="text-xl font-bold">${v.price_per_day}</span><span className="text-sm text-[#AEB9C9]">/day</span></div>
                  <div className="text-xs text-[#AEB9C9] flex items-center gap-1"><Users size={12} /> {v.capacity} seats</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* REVIEWS CAROUSEL */}
      <section className="container-bl py-16 md:py-24">
        <div className="max-w-2xl mb-10">
          <div className="text-sm font-semibold uppercase tracking-widest text-[#E2AB20]">What Ventura County residents say</div>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold font-heading text-white">Real trips. Perfect ratings.</h2>
          <p className="mt-3 text-[#AEB9C9]">Real reviews from real trips — every one verified against a completed booking.</p>
        </div>
        <ReviewsCarousel reviews={reviews} />
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="container-bl py-16 md:py-24">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold uppercase tracking-widest text-[#E2AB20]">Why choose B&L Rentals</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold font-heading text-[#010F27]">Trust built one trip at a time.</h2>
            <p className="mt-4 text-lg text-[#4B5B76] leading-relaxed">
              We've hosted 200+ trips across Ventura County with a perfect 5.0 rating. That's not marketing — it's what happens when you obsess over clean cars, honest communication, and on-time pickups.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {icon: Sparkles, title: "5.0 rating · 200+ trips", body: "Every renter reviews their trip. We haven't dropped below 5 stars in 3 years."},
              {icon: ShieldCheck, title: "Insurance-first booking", body: "We verify your active auto policy before pickup. No surprises at handoff."},
              {icon: MapPin, title: "Oxnard-based, local host", body: "Real photos. Real pickup location. Optional delivery within 25 miles."},
            ].map((f, i) => (
              <div key={i} className="card-bl p-6" data-testid={`why-us-${i}`}>
                <div className="h-10 w-10 rounded-lg bg-[#E2AB20]/10 grid place-items-center text-[#E2AB20]">
                  <f.icon size={20} />
                </div>
                <h3 className="mt-4 font-heading font-semibold text-lg text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-[#AEB9C9] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="container-bl py-16 md:py-24">
        <div className="rounded-2xl bg-[#E2AB20] text-white p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold">Booking takes less than 3 minutes.</h3>
            <p className="mt-2 text-white/85 max-w-xl">Pick your dates, verify your license & insurance, and you're set. Guest checkout available — no account required.</p>
          </div>
          <Link to="/vehicles" className="inline-flex items-center gap-2 bg-white text-[#E2AB20] font-semibold px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors" data-testid="cta-start-booking">
            Start booking <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
